/**
 * persistence/hooks/useSyncStatus.js
 *
 * Reactive sync status hook for UI indicators.
 *
 * Returns the current state of the sync system:
 *   - online:        is the browser online?
 *   - pendingCount:  how many items are waiting to sync?
 *   - failedCount:   how many items permanently failed?
 *   - lastSyncAt:    epoch ms of the last successful API push (from session record)
 *
 * Polls IndexedDB every POLL_INTERVAL_MS.  This is intentionally infrequent
 * — the sync status badge doesn't need to be real-time, and we don't want
 * to add IDB reads to the hot path.
 *
 * Usage:
 *   const { online, pendingCount, failedCount, statusLabel } =
 *     useSyncStatus({ matchId, analystId });
 */

import { useState, useEffect, useRef } from "react";
import { getSyncQueueStatus } from "../managers/SyncManager";
import { getSession }         from "../managers/SessionManager";

const POLL_INTERVAL_MS = 10_000; // poll every 10 seconds

export function useSyncStatus({ matchId, analystId }) {
  const [online,       setOnline]       = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount,  setFailedCount]  = useState(0);
  const [lastSyncAt,   setLastSyncAt]   = useState(null);
  const intervalRef = useRef(null);

  // Online/offline
  useEffect(() => {
    const onOnline  = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Periodic IDB poll
  useEffect(() => {
    if (!matchId || !analystId) return;

    async function poll() {
      try {
        const [queueStatus, session] = await Promise.all([
          getSyncQueueStatus(),
          getSession(matchId, analystId),
        ]);
        setPendingCount(queueStatus.pending);
        setFailedCount(queueStatus.failed);
        setLastSyncAt(session?.lastApiSyncAt ?? null);
      } catch {
        // non-fatal
      }
    }

    poll(); // immediate first check
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [matchId, analystId]);

  // ── Derived labels ─────────────────────────────────────────────────────────

  let statusLabel = "Synced";
  let statusColor = "green";

  if (!online) {
    statusLabel = "Offline — saved locally";
    statusColor = "orange";
  } else if (failedCount > 0) {
    statusLabel = "Sync error — check connection";
    statusColor = "red";
  } else if (pendingCount > 0) {
    statusLabel = `Synching…`;
    statusColor = "blue";
  }

  return {
    online,
    pendingCount,
    failedCount,
    lastSyncAt,
    statusLabel,
    statusColor,
  };
}
